/**
 * Measurements the doctor asked for — docs/33 D1, the owner's yes of
 * 10 October 2026.
 *
 * "Measure your BP at 7 am and write it down": a reminder at the doctor's
 * time, answered by typing the number, listed and exported for the doctor.
 * DrBell records what was typed and nothing else: no chart, no colour, no
 * goal, no "high" or "normal" (docs/05 anti-goals; RX-ADH-03). The bounds
 * below only catch a slip of the finger — 1300 for 130 — and are far wider
 * than any reading means anything about; they never judge one.
 */
export const MEASURES = ['bp', 'sugar', 'weight', 'temperature', 'spo2', 'pulse', 'other'] as const;
export type Measure = (typeof MEASURES)[number];

/** The units each is typed in; the first is the default. `other` takes whatever the doctor said. */
export const MEASURE_UNITS: Record<Measure, readonly string[]> = {
  bp: ['mmHg'],
  sugar: ['mg/dL', 'mmol/L'],
  weight: ['kg', 'lb'],
  temperature: ['°C', '°F'],
  spo2: ['%'],
  pulse: ['bpm'],
  other: [],
};

/** Blood pressure is two numbers, the top and the bottom. */
export function hasSecondValue(m: Measure): boolean {
  return m === 'bp';
}

type Bounds = { min: number; max: number; min2?: number; max2?: number };

/** What a typed number can physically be, by unit — never what it should be. */
const BOUNDS: Record<string, Bounds> = {
  'bp|mmHg': { min: 40, max: 300, min2: 20, max2: 200 },
  'sugar|mg/dL': { min: 10, max: 1000 },
  'sugar|mmol/L': { min: 0.5, max: 55 },
  'weight|kg': { min: 0.5, max: 400 },
  'weight|lb': { min: 1, max: 900 },
  'temperature|°C': { min: 25, max: 45 },
  'temperature|°F': { min: 77, max: 113 },
  'spo2|%': { min: 50, max: 100 },
  'pulse|bpm': { min: 20, max: 250 },
};

export function defaultMeasureUnit(m: Measure): string {
  return MEASURE_UNITS[m][0] ?? '';
}

export interface Reading {
  measure: Measure;
  unit: string;
  value: number;
  /** The bottom number of a blood pressure. */
  value2?: number | null;
}

export type ReadingCheck = { ok: true } | { ok: false; reason: 'bad_unit' | 'missing_second' | 'out_of_range' };

export function checkReading(r: Reading): ReadingCheck {
  const units = MEASURE_UNITS[r.measure];
  if (units.length > 0 && !units.includes(r.unit)) return { ok: false, reason: 'bad_unit' };
  if (r.measure === 'other' && (r.unit.length === 0 || r.unit.length > 20)) return { ok: false, reason: 'bad_unit' };
  if (!Number.isFinite(r.value)) return { ok: false, reason: 'out_of_range' };
  if (hasSecondValue(r.measure) && (r.value2 === undefined || r.value2 === null || !Number.isFinite(r.value2))) {
    return { ok: false, reason: 'missing_second' };
  }
  const b = BOUNDS[`${r.measure}|${r.unit}`];
  if (b) {
    if (r.value < b.min || r.value > b.max) return { ok: false, reason: 'out_of_range' };
    if (b.min2 !== undefined && (r.value2! < b.min2 || r.value2! > b.max2!)) return { ok: false, reason: 'out_of_range' };
  } else if (Math.abs(r.value) > 1e6) {
    return { ok: false, reason: 'out_of_range' };
  }
  return { ok: true };
}

/** "130/85 mmHg", "6.2 mmol/L", "98 %" — the reading as typed, with its unit. */
export function readingText(r: Reading): string {
  const n = (v: number) => String(Math.round(v * 100) / 100);
  const value = hasSecondValue(r.measure) && r.value2 !== undefined && r.value2 !== null ? `${n(r.value)}/${n(r.value2)}` : n(r.value);
  return r.unit ? `${value} ${r.unit}` : value;
}

/**
 * The readings as a spreadsheet for the doctor, like the statement's CSV
 * (statementCsv): a byte-order mark for Excel, and nothing that a
 * spreadsheet could take for a formula.
 */
export function measurementsCsv(
  headers: readonly [when: string, what: string, reading: string, note: string],
  rows: ReadonlyArray<{ at: string; name: string; reading: string; note?: string | null }>,
): string {
  const cell = (v: string) => {
    const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const lines = [[...headers], ...rows.map((r) => [r.at, r.name, r.reading, r.note ?? ''])];
  return `﻿${lines.map((l) => l.map(cell).join(',')).join('\r\n')}\r\n`;
}
